<!--
  Abre un submenú al pasar el cursor o pulsar una opción de Dropdown. Su popover se anida
  en el menú principal para que abrirlo conserve el padre y cierre los submenús hermanos.

  Se coloca a la derecha y crece hacia arriba, con ajuste al lado contrario si falta
  espacio.
-->
<script lang="ts" module>
  // Cada submenú ancla su propia lista: dos abiertos a la vez no pueden
  // compartir un anchor-name.
  let anchorSeq = 0;
</script>

<script lang="ts">
  import type { Snippet } from "svelte";

  import { cx } from "../../lib/cx";
  import Icon from "../Icon.svelte";

  let {
    icon,
    value,
    disabled = false,
    class: className,
    menuClass,
    children,
    content,
  }: {
    icon?: Snippet;
    /** Lo que está elegido ahora, apagado a la derecha del rótulo. */
    value?: string;
    disabled?: boolean;
    class?: string;
    menuClass?: string;
    /** El rótulo de la fila. */
    children: Snippet;
    /** La lista; recibe con qué cerrarla. */
    content: Snippet<[() => void]>;
  } = $props();

  let open = $state(false);
  let list = $state<HTMLDivElement | null>(null);

  anchorSeq += 1;
  const anchorName = `--menu-sub-${anchorSeq}`;

  /** Abre el submenú si no está deshabilitado. */
  const show = () => {
    if (!disabled && !open) list?.showPopover();
  };
  /** Cierra el submenú. */
  const close = () => list?.hidePopover();
  /** Abre o cierra el submenú. */
  const toggle = () => (open ? close() : show());
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="wrap-menu-sub" style:anchor-name={anchorName} onmouseenter={show} onmouseleave={close}>
  <button
    type="button"
    {disabled}
    aria-haspopup="menu"
    aria-expanded={open}
    onclick={toggle}
    class={cx(className, "menu-sub-trigger", open && "is-open")}
  >
    {@render icon?.()}
    <span class="menu-sub-label">{@render children()}</span>
    {#if value}
      <span class="menu-sub-value">{value}</span>
    {/if}
    <Icon name="arrow-right-01" size={12} class="menu-sub-caret" />
  </button>

  <div
    bind:this={list}
    popover
    role="menu"
    class={cx("menu menu-sub", menuClass)}
    style:position-anchor={anchorName}
    ontoggle={(e) => {
      open = (e as ToggleEvent).newState === "open";
    }}
  >
    {@render content(close)}
  </div>
</div>

<style>
  .wrap-menu-sub {
    display: flex;
  }

  .menu-sub-trigger {
    &.is-open {
      background: var(--bg-field);
      color: var(--text-primary);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }

    & .menu-sub-label {
      /* flex-shrink: 0 — texto que no debe partirse en dos líneas. */
      flex-shrink: 0;
    }

    & .menu-sub-value {
      min-width: 0;
      flex: 1;
      overflow: hidden;
      color: var(--text-muted);
      font-weight: 400;
      text-align: right;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* El Icon es un componente: su clase sale del ámbito. */
  :global(.menu-sub-caret) {
    margin-left: auto;
    opacity: 0.6;
  }

  /*
    Pisa la posición de `.menu` (debajo del disparador): a la derecha de la
    fila, con el borde de abajo a su altura. Sin separación: el hueco entre la
    fila y la lista haría saltar el `mouseleave` al cruzarlo.
  */
  .menu-sub {
    margin: 0;
    max-height: var(--menu-max-height, 20rem);
    overflow-y: auto;
    position-area: right span-top;
    align-self: end;
    justify-self: start;
    transform-origin: left bottom;
    position-try-fallbacks:
      flip-block,
      flip-inline,
      flip-block flip-inline;
  }
</style>
